import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

interface Buyer { id: string; companyName: string; contactName: string; contactEmail: string; address: string; city: string; country: string; }

interface LineItem {
  productCode: string;
  icoNumber: string;
  description: string;
  hsCode: string;
  numberOfBags: string;
  packingType: string;
  netWeightKg: string;
  pricePerKg: string;
}

const emptyLine: LineItem = {
  productCode: '', icoNumber: '', description: '', hsCode: '090111000',
  numberOfBags: '', packingType: '40 kg Paper Bag + GP', netWeightKg: '', pricePerKg: '',
};

export const ExportInvoiceForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    invoiceNumber: '', buyerId: '',
    consigneeName: '', consigneeAddress: '', consigneeContact: '',
    notifyPartyName: '', notifyPartyAddress: '', notifyPartyContact: '',
    shippingMethod: 'Sea Maritime', containerType: 'FCL',
    vesselName: '', voyageNumber: '',
    portOfLoading: 'Mombasa', portOfDischarge: '', departureDate: '',
    finalDestination: '', billOfLading: '', buyerReference: '',
    currency: 'USD', incoterms: 'FOB Mombasa', notes: '',
  });
  const [lineItems, setLineItems] = useState<LineItem[]>([{ ...emptyLine }]);
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/admin/buyers', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setBuyers(Array.isArray(data) ? data : []))
      .catch(() => {});

    if (isEdit) {
      fetch(`/api/admin/export-invoices?id=${id}`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          setForm({
            invoiceNumber: data.invoiceNumber || '',
            buyerId: data.buyerId || '',
            consigneeName: data.consigneeName || '',
            consigneeAddress: data.consigneeAddress || '',
            consigneeContact: data.consigneeContact || '',
            notifyPartyName: data.notifyPartyName || '',
            notifyPartyAddress: data.notifyPartyAddress || '',
            notifyPartyContact: data.notifyPartyContact || '',
            shippingMethod: data.shippingMethod || 'Sea Maritime',
            containerType: data.containerType || 'FCL',
            vesselName: data.vesselName || '',
            voyageNumber: data.voyageNumber || '',
            portOfLoading: data.portOfLoading || 'Mombasa',
            portOfDischarge: data.portOfDischarge || '',
            departureDate: data.departureDate || '',
            finalDestination: data.finalDestination || '',
            billOfLading: data.billOfLading || '',
            buyerReference: data.buyerReference || '',
            currency: data.currency || 'USD',
            incoterms: data.incoterms || 'FOB Mombasa',
            notes: data.notes || '',
          });
          if (data.lineItems?.length) {
            setLineItems(data.lineItems.map((li: any) => ({
              productCode: li.productCode || '',
              icoNumber: li.icoNumber || '',
              description: li.description || '',
              hsCode: li.hsCode || '090111000',
              numberOfBags: String(li.numberOfBags || ''),
              packingType: li.packingType || '40 kg Paper Bag + GP',
              netWeightKg: li.netWeightKg || '',
              pricePerKg: li.pricePerKg || '',
            })));
          }
        });
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onBuyerSelect = (buyerId: string) => {
    setForm(prev => ({ ...prev, buyerId }));
    const buyer = buyers.find(b => b.id === buyerId);
    if (buyer) {
      const addr = [buyer.address, buyer.city, buyer.country].filter(Boolean).join('\n');
      setForm(prev => ({
        ...prev,
        buyerId,
        consigneeName: prev.consigneeName || buyer.companyName,
        consigneeAddress: prev.consigneeAddress || addr,
        consigneeContact: prev.consigneeContact || buyer.contactName,
      }));
    }
  };

  const updateLine = (i: number, field: keyof LineItem, value: string) => {
    setLineItems(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      if (field === 'numberOfBags') {
        const bags = parseInt(value) || 0;
        const bagWeight = next[i].packingType.match(/(\d+)\s*kg/)?.[1];
        if (bagWeight) {
          next[i].netWeightKg = String(bags * parseInt(bagWeight));
        }
      }
      return next;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...emptyLine }]);
  const removeLine = (i: number) => setLineItems(prev => prev.filter((_, j) => j !== i));

  const totalBags = lineItems.reduce((s, li) => s + (parseInt(li.numberOfBags) || 0), 0);
  const totalWeight = lineItems.reduce((s, li) => s + (parseFloat(li.netWeightKg) || 0), 0);
  const totalAmount = lineItems.reduce((s, li) => {
    return s + (parseFloat(li.netWeightKg) || 0) * (parseFloat(li.pricePerKg) || 0);
  }, 0);

  const fmtCurrency = (amount: number) => {
    const symbols: Record<string, string> = { USD: '$', EUR: '\u20AC', GBP: '\u00A3', KES: 'KES ' };
    const sym = symbols[form.currency] || form.currency + ' ';
    return `${sym}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    if (!form.invoiceNumber.trim()) {
      setError('Invoice number is required');
      setSaving(false);
      return;
    }

    const validItems = lineItems.filter(li => li.description && li.netWeightKg && li.pricePerKg);
    if (validItems.length === 0) {
      setError('Add at least one line item');
      setSaving(false);
      return;
    }

    const body = isEdit
      ? { id, ...form, lineItems: validItems }
      : { ...form, lineItems: validItems };

    try {
      const res = await fetch('/api/admin/export-invoices', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to save');
      navigate('/admin/export-invoices');
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 border border-[#E8E6E2] rounded-xl bg-[#FAFAF8] text-[#1A1A1A] text-sm focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all placeholder-[#C4C4C4]";
  const smallInput = "w-full px-2.5 py-2 border border-[#E8E6E2] rounded-lg bg-[#FAFAF8] text-[#1A1A1A] text-xs focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:bg-white transition-all placeholder-[#C4C4C4]";
  const labelClass = "block text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold mb-1.5";

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{isEdit ? 'Edit Commercial Invoice' : 'New Commercial Invoice'}</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">Export commercial invoice for shipment</p>
        </div>
        <div className="flex gap-3">
          {isEdit && (
            <button
              onClick={() => window.open(`/api/admin/export-invoices?id=${id}&action=pdf`, '_blank')}
              className="px-4 py-2.5 border border-[#E8E6E2] rounded-xl text-sm text-[#6B6B6B] hover:bg-[#F5F3F0] hover:border-[#E0DDD8] transition-all font-medium"
            >
              Preview PDF
            </button>
          )}
          <button onClick={() => navigate('/admin/export-invoices')} className="text-sm text-[#A3A3A3] hover:text-[#1A1A1A] transition-colors font-medium">
            Back
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm font-medium flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Invoice & Buyer Reference */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Invoice Details</h2>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className={labelClass}>Invoice Number *</label>
              <input name="invoiceNumber" value={form.invoiceNumber} onChange={handleChange} required placeholder="Keynote25-114" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Buyer</label>
              <select name="buyerId" value={form.buyerId} onChange={e => onBuyerSelect(e.target.value)} className={inputClass}>
                <option value="">Select buyer...</option>
                {buyers.map(b => <option key={b.id} value={b.id}>{b.companyName}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>B/L Number</label>
              <input name="billOfLading" value={form.billOfLading} onChange={handleChange} placeholder="262370162" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Buyer Reference</label>
              <input name="buyerReference" value={form.buyerReference} onChange={handleChange} placeholder="ref 4346P01452 to 4346P01464" className={inputClass} />
            </div>
          </div>
        </div>

        {/* Consignee / Notify */}
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
            <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Consignee</h2>
            <div className="space-y-3">
              <div><label className={labelClass}>Name</label><input name="consigneeName" value={form.consigneeName} onChange={handleChange} placeholder="Keynote Coffee Limited" className={inputClass} /></div>
              <div><label className={labelClass}>Address</label><textarea name="consigneeAddress" value={form.consigneeAddress} onChange={handleChange} rows={3} placeholder="Cable House, 5th Floor&#10;London, EC2M 1JJ&#10;United Kingdom" className={inputClass} /></div>
              <div><label className={labelClass}>Contact</label><input name="consigneeContact" value={form.consigneeContact} onChange={handleChange} placeholder="Lola Dobney" className={inputClass} /></div>
            </div>
          </div>
          <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
            <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Notify Party</h2>
            <div className="space-y-3">
              <div><label className={labelClass}>Name</label><input name="notifyPartyName" value={form.notifyPartyName} onChange={handleChange} placeholder="Vollers UK Limited" className={inputClass} /></div>
              <div><label className={labelClass}>Address</label><textarea name="notifyPartyAddress" value={form.notifyPartyAddress} onChange={handleChange} rows={3} placeholder="Rougham Industrial Estate&#10;Bury St Edmunds, IP30 9ND&#10;United Kingdom" className={inputClass} /></div>
              <div><label className={labelClass}>Contact</label><input name="notifyPartyContact" value={form.notifyPartyContact} onChange={handleChange} placeholder="Trina Briggs" className={inputClass} /></div>
            </div>
          </div>
        </div>

        {/* Shipping */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Shipping Details</h2>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className={labelClass}>Method</label>
              <select name="shippingMethod" value={form.shippingMethod} onChange={handleChange} className={inputClass}>
                <option value="Sea Maritime">Sea Maritime</option>
                <option value="Air">Air</option>
                <option value="Road">Road</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Container Type</label>
              <select name="containerType" value={form.containerType} onChange={handleChange} className={inputClass}>
                <option value="FCL">FCL</option>
                <option value="LCL">LCL</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Vessel Name</label>
              <input name="vesselName" value={form.vesselName} onChange={handleChange} placeholder="XIN HUANG PU" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Voyage No.</label>
              <input name="voyageNumber" value={form.voyageNumber} onChange={handleChange} placeholder="550E" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Port of Loading</label>
              <input name="portOfLoading" value={form.portOfLoading} onChange={handleChange} placeholder="Mombasa" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Port of Discharge</label>
              <input name="portOfDischarge" value={form.portOfDischarge} onChange={handleChange} placeholder="London Gateway Port" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Departure Date</label>
              <input name="departureDate" type="date" value={form.departureDate} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Final Destination</label>
              <input name="finalDestination" value={form.finalDestination} onChange={handleChange} placeholder="United Kingdom" className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className={labelClass}>Currency</label>
              <select name="currency" value={form.currency} onChange={handleChange} className={inputClass}>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (&euro;)</option>
                <option value="GBP">GBP (&pound;)</option>
                <option value="KES">KES</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Incoterms</label>
              <select name="incoterms" value={form.incoterms} onChange={handleChange} className={inputClass}>
                <option value="FOB Mombasa">FOB Mombasa</option>
                <option value="CIF">CIF</option>
                <option value="CFR">CFR</option>
                <option value="EXW">EXW</option>
                <option value="FCA">FCA</option>
              </select>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Line Items</h2>

          <div className="grid grid-cols-[1fr_1fr_1.2fr_0.8fr_0.6fr_1fr_0.7fr_0.7fr_0.7fr_32px] gap-2 mb-2 px-1">
            {['Product Code', 'ICO Number', 'Description', 'HS Code', 'Bags', 'Packing', 'Weight (kg)', 'Price/kg', 'Total', ''].map((h, i) => (
              <span key={i} className={`${labelClass} ${i >= 6 ? 'text-right' : ''}`}>{h}</span>
            ))}
          </div>

          <div className="space-y-1.5">
            {lineItems.map((li, i) => {
              const lineTotal = (parseFloat(li.netWeightKg) || 0) * (parseFloat(li.pricePerKg) || 0);
              return (
                <div key={i} className="grid grid-cols-[1fr_1fr_1.2fr_0.8fr_0.6fr_1fr_0.7fr_0.7fr_0.7fr_32px] gap-2 items-center p-2.5 bg-[#FAFAF8] rounded-xl border border-[#E8E6E2]/40">
                  <input value={li.productCode} onChange={e => updateLine(i, 'productCode', e.target.value)} placeholder="Nandi AA" className={smallInput} />
                  <input value={li.icoNumber} onChange={e => updateLine(i, 'icoNumber', e.target.value)} placeholder="37/4CGM/GM/014" className={smallInput} />
                  <input value={li.description} onChange={e => updateLine(i, 'description', e.target.value)} placeholder="Washed Coffee" className={smallInput} />
                  <input value={li.hsCode} onChange={e => updateLine(i, 'hsCode', e.target.value)} placeholder="090111000" className={smallInput} />
                  <input type="number" value={li.numberOfBags} onChange={e => updateLine(i, 'numberOfBags', e.target.value)} placeholder="15" className={smallInput} />
                  <input value={li.packingType} onChange={e => updateLine(i, 'packingType', e.target.value)} className={smallInput} />
                  <input type="number" step="0.01" value={li.netWeightKg} onChange={e => updateLine(i, 'netWeightKg', e.target.value)} placeholder="600" className={`${smallInput} text-right`} />
                  <input type="number" step="0.01" value={li.pricePerKg} onChange={e => updateLine(i, 'pricePerKg', e.target.value)} placeholder="9.60" className={`${smallInput} text-right`} />
                  <p className="text-xs font-semibold text-[#1A1A1A] text-right tabular-nums pr-1">{fmtCurrency(lineTotal)}</p>
                  <button type="button" onClick={() => removeLine(i)} disabled={lineItems.length === 1} className="w-8 h-8 flex items-center justify-center text-[#C4C4C4] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                    &times;
                  </button>
                </div>
              );
            })}
          </div>

          <button type="button" onClick={addLine} className="mt-3 px-4 py-2.5 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] border border-dashed border-[#E8E6E2] hover:border-[#A3A3A3] rounded-xl transition-all w-full">
            + Add line item
          </button>

          <div className="mt-6 pt-5 border-t border-[#E8E6E2]/60 flex justify-end">
            <div className="w-80 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#A3A3A3]">Total Bags</span>
                <span className="text-[#1A1A1A] tabular-nums font-medium">{totalBags.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#A3A3A3]">Total Net Weight</span>
                <span className="text-[#1A1A1A] tabular-nums font-medium">{totalWeight.toLocaleString('en-US', { minimumFractionDigits: 2 })} kg</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-3 border-t-2 border-[#1A1A1A]">
                <span className="text-[#1A1A1A]">Consignment Total</span>
                <span className="text-[#1A1A1A] tabular-nums">{fmtCurrency(totalAmount)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Notes</h2>
          <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Additional shipping notes, quality certificates..." className={inputClass} />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={saving} className="px-8 py-3 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all disabled:opacity-40 shadow-sm shadow-black/10">
            {saving ? 'Saving...' : isEdit ? 'Update Invoice' : 'Create Invoice'}
          </button>
          <button type="button" onClick={() => navigate('/admin/export-invoices')} className="px-6 py-3 border border-[#E8E6E2] rounded-xl text-sm text-[#6B6B6B] hover:bg-[#F5F3F0] hover:border-[#E0DDD8] transition-all font-medium">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
